import React, { useEffect, useState } from 'react'
import { ActionableTable } from '../../../Table/ActionableTable'
import { useTranslation } from 'react-i18next'
import { useLoadingIds } from '@hooks/useLoadingIds'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import { CashAmount, OrderByDirection } from '@swagger'
import useLoadingContext from '../../../../hooks/useLoadingContext'
import getOrderByClause from '../../../../utils/getOrderByClause'
import { paymentsApi } from '@api/swagger'
import { usePagination } from '@hooks/usePagination'
import { getLcdLicensingPaymentHistoryColumns } from './columns'
import { getLcdLicensingPaymentHistoryRowActions } from './rowActions'
import { getLcdLicensingPaymentHistoryRows } from './rows'
import TransactionModal from '../../../Modals/TransactionModal'
import ProgramOption from '../../../Interfaces/ProgramOption'
import { Payment, Payment as PaymentMade } from '../../../Interfaces/Payment'
import { useAuth } from '../../../Routes/AuthProvider'
import { Transaction } from '../BillingHistorySummaryTable'
import ViewBillingInfoModal from '../../../Families/ViewBillingInfoModal'
import { reinterpretYearMonthDayAsLocalTime } from '@utils/reinterpretYearMonthDayAsLocalTime'
import { useParams } from 'react-router'
import { SelectChangeEvent } from '@mui/material/Select'
import LcdLicensingPaymentHistoryFilters from './LcdLicensingPaymentHistoryFilters'

const MAP_COLUMN_TO_API_FIELD: Record<string, string> = {
  date: 'begunAt',
  program: 'program',
}

interface LcdLicensingPaymentHistoryProps {
  licensingProgramOptions?: ProgramOption[]
  triggerRefetch?: boolean
}

const LcdLicensingPaymentHistory: React.FC<LcdLicensingPaymentHistoryProps> = ({
  licensingProgramOptions = [],
  triggerRefetch: triggerRefetchProp,
}) => {
  const { actorKey } = useParams()
  const { t } = useTranslation()
  const { handleError } = useAppSnackbar()
  const { permissionAbility, userDetails } = useAuth()

  const canEditAll = permissionAbility.can('updateLicensingPayment', 'Payment')
  const canEditPayments =
    canEditAll ||
    permissionAbility.can('updateLicensingPaymentOnlyDownline', 'Payment')

  const {
    PaymentApi: {
      fetchLicensingLcdPayments: fetchLicensingLcdPaymentsLoadingId,
    },
  } = useLoadingIds()

  const [paymentHistory, setPaymentHistory] = useState<PaymentMade[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<PaymentMade>()
  const [selectedPrograms, setSelectedPrograms] = useState<ProgramOption[]>([])
  const [dateFilter, setDateFilter] = useState('')
  const [programFilter, setProgramFilter] = useState('')
  const { pagination, setPage, setPageSize, setOrderBy, setTotalCount } =
    usePagination({
      initialOrderBy: [{ begunAt: OrderByDirection.Desc }],
    })
  const [billingHistoryViewModalOpen, setBillingHistoryViewModalOpen] =
    useState(false)
  const [transaction, setTransaction] = useState<Transaction>({
    paymentMethod: '',
    date: new Date(),
    status: '',
    amount: '',
    orderId: '',
    parentId: -1,
    studentNames: [''],
  })

  const noResultsMessage = t(
    'LcdLicensingPaymentHistory.NoTransactionHistory',
    'No transaction history'
  )

  const errorMessage = t(
    'LcdLicensingPaymentHistory.Error',
    'An error occurred while attempting to load the payment history'
  )

  const fetchPaymentHistory = async () => {
    try {
      const data = await paymentsApi.fetchLicensingLcdPayments({
        page: pagination.page,
        pageSize: pagination.pageSize,
        orderBy: getOrderByClause(pagination.orderBy),
        actorKey: actorKey ? +actorKey : undefined,
        date: dateFilter || undefined,
        program: programFilter || undefined,
      })

      setPaymentHistory(
        (data.licensingPaymentsData ?? []).map((licensingHistory) => ({
          ...licensingHistory.paymentInformation,
          paymentsMade: [
            {
              ...licensingHistory,
              paymentKey: licensingHistory.licensingPaymentKey,
              studentKey: -1,
            },
          ],
          licensingPayment: licensingHistory,
        })) as Payment[]
      )
      setTotalCount(data.pagination?.totalCount ?? 0)
      setHasLoaded(true)
    } catch (error) {
      await handleError(error, errorMessage)
    }
  }

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilter(event.target.value)
  }

  const handleProgramChange = (event: SelectChangeEvent<string>) => {
    setProgramFilter(event.target.value)
  }

  const { triggerFetch, isLoading } = useLoadingContext({
    asyncFunction: fetchPaymentHistory,
    loadingId: fetchLicensingLcdPaymentsLoadingId,
  })
  const showLoading = isLoading && !hasLoaded

  useEffect(() => {
    triggerFetch()
  }, [
    pagination.page,
    pagination.pageSize,
    pagination.orderBy,
    triggerFetch,
    triggerRefetchProp,
    dateFilter,
    programFilter,
  ])

  const handleEdit = (payment: Payment) => {
    setSelectedPayment({
      ...payment,
      amount: payment.licensingPayment?.amount as CashAmount,
    })
    setSelectedPrograms(
      licensingProgramOptions?.filter(
        (licensingProgramOption) =>
          payment.paymentsMade[0].programKey ===
          licensingProgramOption.programKey
      ) ?? []
    )
    setIsTransactionModalOpen(!isTransactionModalOpen)
  }

  const handleMoreInfo = (
    paymentMethod: string,
    date: Date,
    status: string,
    amount: string,
    orderId: string,
    parentKey: number,
    studentNames: string[]
  ) => {
    setTransaction({
      paymentMethod,
      date,
      status,
      amount,
      orderId,
      parentId: parentKey,
      studentNames,
    })
    setBillingHistoryViewModalOpen(true)
  }

  const columns = getLcdLicensingPaymentHistoryColumns(t)
  const rows = getLcdLicensingPaymentHistoryRows(t, paymentHistory)
  const rowActions = getLcdLicensingPaymentHistoryRowActions({
    t,
    handleEdit,
    canEditPayments,
    canEditAll,
    handleMoreInfo,
    userKey: userDetails.userKey,
  })

  return (
    <>
      {isTransactionModalOpen && (
        <TransactionModal
          isOpen
          onClose={() => setIsTransactionModalOpen(false)}
          payment={selectedPayment}
          isLicensingVariant
          programOptions={licensingProgramOptions}
          selectedPrograms={selectedPrograms}
        />
      )}

      <ViewBillingInfoModal
        transactionType={transaction.paymentMethod}
        date={reinterpretYearMonthDayAsLocalTime(transaction.date)}
        status={transaction.status}
        amount={transaction.amount}
        orderId={transaction.orderId}
        students={transaction.studentNames ?? []}
        isOpen={billingHistoryViewModalOpen}
        onClose={() => setBillingHistoryViewModalOpen(false)}
      />

      <LcdLicensingPaymentHistoryFilters
        dateFilter={dateFilter}
        programFilter={programFilter}
        onDateChange={handleDateChange}
        onProgramChange={handleProgramChange}
      />

      <ActionableTable
        isLoading={showLoading}
        rowActions={rowActions}
        columns={columns}
        noResultsMessage={noResultsMessage}
        customCssTable={{
          border: 'none',
        }}
        rowHeight={40}
        rows={rows}
        handlePaginationChange={(paginationInfo) => {
          const { page, pageSize, orderBy: newOrderBy } = paginationInfo
          if (newOrderBy?.[0]) {
            const orderByEntry = newOrderBy[0]
            const [fieldName, direction] = Object.entries(orderByEntry)[0] ?? []

            if (fieldName && direction) {
              const mappedField =
                MAP_COLUMN_TO_API_FIELD[fieldName] ?? fieldName

              setOrderBy([
                {
                  [mappedField]: direction.toUpperCase() as OrderByDirection,
                },
              ])
              setPage(page + 1)
              setPageSize(pageSize)
              return
            }
          }

          setOrderBy([])
          setPage(page + 1)
          setPageSize(pageSize)
        }}
        pagination={{
          ...pagination,
          page: pagination.page - 1,
        }}
      />
    </>
  )
}

export default LcdLicensingPaymentHistory
